import { useMemo, useState } from 'react';
import { useListFetch, useDebouncedValue } from '../../../hooks';
import { getAllSystemPrompts } from '../../../api';
import {
  promptLibraryVariables,
  VariablesEnum,
  VARIABLES_TAB,
  PROMPTS_TAB,
} from '../const';
import { searchArray } from '../../../utilities';
import type { SystemPrompt } from '../../../api';

type TabsEnum = VariablesEnum.variables | VariablesEnum.prompts;

type UseVariablesTabsParams = {
  isPromptLibraryAccess: boolean;
};

export const useVariablesTabs = ({
  isPromptLibraryAccess,
}: UseVariablesTabsParams) => {
  const [currentTab, setCurrentTab] = useState<TabsEnum>(
    VariablesEnum.variables
  );
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);

  const { nodes, loading, intializing } = useListFetch<SystemPrompt>(
    async () => {
      const arr =
        currentTab === VariablesEnum.prompts
          ? await getAllSystemPrompts()
          : undefined;
      return { content: arr };
    },
    {
      deps: [currentTab],
    }
  );

  const handleTabChange = (tab: TabsEnum) => {
    setCurrentTab(tab);
    setSearch('');
  };

  const varibleTabs = useMemo(() => {
    return isPromptLibraryAccess
      ? [VARIABLES_TAB, PROMPTS_TAB]
      : [VARIABLES_TAB];
  }, [isPromptLibraryAccess]);

  const filteredVariables = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return promptLibraryVariables;
    }
    return promptLibraryVariables.filter(item =>
      item.toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [debouncedSearch]);

  const allPromptData = useMemo(
    () =>
      nodes?.map(prompt => ({
        id: prompt.identifier || '',
        title: prompt.title || '',
      })) || [],
    [nodes]
  );

  const filteredPromptData = useMemo(
    () => searchArray(allPromptData, debouncedSearch, ['title']),
    [allPromptData, debouncedSearch]
  );

  const promptTitles = filteredPromptData.map(p => p.title);

  return {
    currentTab,
    search,
    setSearch,
    debouncedSearch,
    loading,
    intializing,
    varibleTabs,
    filteredVariables,
    filteredPromptData,
    promptTitles,
    handleTabChange,
  };
};
